import { useState } from 'react'
import type { Medium, Show } from '../api/types'
import { useSaveShow } from '../hooks/useShows'
import { languageName } from '../utils/format'
import { Button, ErrorNote, Field } from './ui'

const FORMATS: { value: Medium; label: string; hint: string }[] = [
  { value: 'audio', label: 'Audio', hint: 'Clips become audiograms: waveform and captions over your artwork.' },
  { value: 'video', label: 'Video', hint: 'Episodes also get a 720p proxy, and clips are reframed with captions.' },
]

/** Apple Podcasts categories (a subcategory after `>`). */
const CATEGORIES = [
  'Arts', 'Business', 'Comedy', 'Education', 'Fiction', 'Health & Fitness', 'History', 'Kids & Family', 'Kids & Family > Parenting',
  'Leisure', 'Music', 'News', 'Religion & Spirituality', 'Science', 'Society & Culture', 'Society & Culture > Documentary',
  'Society & Culture > Personal Journals', 'Sports', 'Technology', 'True Crime', 'TV & Film',
]

/** Create a show, or edit one when `show` is given. */
/** The codes most shows use, offered as suggestions; any valid tag is allowed. */
const COMMON_LANGUAGES = ['en', 'en-gb', 'es', 'es-mx', 'pt-br', 'fr', 'de', 'it', 'nl', 'sv', 'pl', 'ru', 'uk', 'tr', 'ar', 'hi', 'ja', 'ko', 'zh']

/** Say what the code means, so a typo is visible before it reaches a directory. */
function languageHint(code: string): string {
  const named = languageName(code.trim())
  const unknown = !code.trim() || named.toLowerCase() === code.trim().toLowerCase()
  return unknown ? 'A code such as en, en-gb or es. Apps and directories use it to file the show.' : `${named}. Apps and directories use it to file the show.`
}

export function ShowForm({ workspaceId, show, onDone }: { workspaceId: string; show?: Show; onDone: (show: Show) => void }) {
  const [title, setTitle] = useState(show?.title ?? '')
  const [description, setDescription] = useState(show?.description ?? '')
  const [medium, setMedium] = useState<Medium>(show?.medium ?? 'audio')
  const [voicePrompt, setVoicePrompt] = useState(show?.voice_prompt ?? '')
  const [author, setAuthor] = useState(show?.author ?? '')
  const [ownerEmail, setOwnerEmail] = useState(show?.owner_email ?? '')
  const [category, setCategory] = useState(show?.category ?? '')
  const [language, setLanguage] = useState(show?.language ?? 'en')
  const [explicit, setExplicit] = useState(show?.explicit ?? false)
  const save = useSaveShow(workspaceId, show?.id)
  return (
    <form
      className="stack"
      onSubmit={(event) => {
        event.preventDefault()
        const base = { title: title.trim(), description: description.trim(), medium, voice_prompt: voicePrompt.trim() }
        const directory = { author: author.trim(), owner_email: ownerEmail.trim(), category, language: language.trim(), explicit }
        save.mutate(show ? { ...base, ...directory } : base, { onSuccess: onDone })
      }}
    >
      <Field label="Title">
        <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} placeholder="Sunday Stories" required />
      </Field>
      <fieldset className="choice">
        <legend className="field-label">Format</legend>
        {FORMATS.map((format) => (
          <label key={format.value} className={`choice-option${medium === format.value ? ' is-selected' : ''}`}>
            <input type="radio" name="medium" value={format.value} checked={medium === format.value} onChange={() => setMedium(format.value)} />
            <span>
              <strong>{format.label}</strong>
              <span className="muted small">{format.hint}</span>
            </span>
          </label>
        ))}
      </fieldset>
      <Field label="Description" hint="Shown on the show page and in the RSS feed.">
        <textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} maxLength={5000} />
      </Field>
      <Field label="Voice" hint="Guides everything written for this show: tone, audience, words to use or avoid.">
        <textarea
          rows={3}
          value={voicePrompt}
          onChange={(e) => setVoicePrompt(e.target.value)}
          maxLength={2000}
          placeholder="Warm and plain-spoken, for parents. Never say 'journey'."
        />
      </Field>
      {show && (
        <fieldset className="stack">
          <legend className="field-label">For podcast directories</legend>
          <div className="grid-2">
            <Field label="Author" hint="Shown as the show's creator.">
              <input value={author} onChange={(e) => setAuthor(e.target.value)} maxLength={200} />
            </Field>
            <Field label="Owner email" hint="Directories send ownership checks here. Not shown publicly.">
              <input type="email" value={ownerEmail} onChange={(e) => setOwnerEmail(e.target.value)} maxLength={320} />
            </Field>
            <Field label="Category">
              <select value={category} onChange={(e) => setCategory(e.target.value)}>
                <option value="">Choose one</option>
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </Field>
            <Field label="Language" hint={languageHint(language)}>
              <input value={language} onChange={(e) => setLanguage(e.target.value)} maxLength={10} list="show-languages" />
              <datalist id="show-languages">
                {COMMON_LANGUAGES.map((code) => (
                  <option key={code} value={code}>
                    {languageName(code)}
                  </option>
                ))}
              </datalist>
            </Field>
          </div>
          <label className="row small">
            <input type="checkbox" checked={explicit} onChange={(e) => setExplicit(e.target.checked)} /> Explicit content
          </label>
        </fieldset>
      )}
      {save.error && <ErrorNote error={save.error} />}
      <div className="row">
        <Button type="submit" busy={save.isPending} disabled={!title.trim()}>{show ? 'Save show' : 'Create show'}</Button>
      </div>
    </form>
  )
}
